/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectSpread2 as _objectSpread2, slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';

/**
 * This provides a utility for sending requests. It's essentially a wrapper around `fetch` that
 * adds in some useful features such as response caching, stringifying request bodies, adding
 * account related headers, and parsing response bodies. By default caching is enabled and all
 * respones from GET requests are stored in memory for 30 seconds. If the same request is made
 * again while the previously cached response exists then the cached response is returned.
 *
 * The `url` is required, and additional options include:
 *   `method`: request method (GET if not provided)
 *   `headers`: request headers
 *   `body`: request body (For PUT, POST, or PATCH requests)
 *   `signal`: a signal from an AbortController to allow aborting the request
 *   `cache`: an object with properties
 *     id: the cache id (auto generated based on url and account id if not provided)
 *     expiration: number of milliseconds to hold the response in memory (30000 by default)
 *     disable: set to `true` to disable caching
 *
 * Examples:
 *
 * import { send } from 'src/Utilities/request';
 * // OR import requestUtils from 'src/Utilities/request';
 *
 * // List kubernetes clusters, only cache for 10 seconds
 * send({
 *   url: '/kubernetes/api/clusters',
 *   cache: { expiration: 10000 },
 * })
 *   .then(result => {
 *     // Do something with the result
 *   })
 *   .catch(err => {
 *     if (err.responseJSON) {
 *       // Do something with the JSON error data returned by the API
 *     }
 *   })
 *
 * // Update a cluster
 * requestUtils.put('/kubernetes/api/clusters/abcd1234/nodes', {
 *   body: { action: 'update', ids: nodeIds },
 * })
 *   .then(() => {
 *     // Success!
 *   })
 *   .catch(err => {
 *     if (err.responseJSON) {
 *       // Do something with the JSON error data returned by the API
 *     }
 *   })
 */

var responseCache = {};
var getCacheKey = function getCacheKey(url, options, cache) {
  return (cache === null || cache === void 0 ? void 0 : cache.id) || "".concat(url, "-").concat(options.headers['ibm-account-context'] || options.headers['x-bss-account']);
};
var timeout = function timeout(callback, ms) {
  var timer = window.setTimeout(callback, ms);
  return function () {
    return window.clearTimeout(timer);
  };
};
var save = function save(key, result, expiration) {
  responseCache[key] = {
    result: result,
    clearTimeout: timeout(function () {
      delete responseCache[key];
    }, expiration || 30000)
  };
};
var getCachedResult = function getCachedResult(key) {
  var _responseCache$key;
  return (_responseCache$key = responseCache[key]) === null || _responseCache$key === void 0 ? void 0 : _responseCache$key.result;
};
var clearCache = function clearCache() {
  Object.keys(responseCache).forEach(function (key) {
    responseCache[key].clearTimeout();
    delete responseCache[key];
  });
};
var getAccountData = function getAccountData() {
  return new Promise(function (resolve) {
    window.header.whenAccountReady(function (data) {
      return resolve(data);
    });
  });
};
var processResult = function processResult(result) {
  var contentType = result.headers.get('content-type');
  if (contentType && contentType.indexOf('application/json') > -1) return result.json();
  return result.text();
};
var send = function send(_ref) {
  var url = _ref.url,
    method = _ref.method,
    headers = _ref.headers,
    body = _ref.body,
    signal = _ref.signal,
    cache = _ref.cache;
  return getAccountData().then(function (accountData) {
    var normalizedHeaders = {};
    if (headers) {
      Object.keys(headers).forEach(function (header) {
        normalizedHeaders[header.toLowerCase()] = headers[header];
      });
    }
    var requestHeaders = _objectSpread2({
      accept: 'application/json'
    }, normalizedHeaders);
    if (accountData) {
      var _accountData$account, _accountData$account2;
      if (accountData.selectedAccountGuid) requestHeaders['x-bss-account'] = accountData.selectedAccountGuid;
      if (accountData !== null && accountData !== void 0 && (_accountData$account = accountData.account) !== null && _accountData$account !== void 0 && _accountData$account.softlayerAccountId) requestHeaders['x-ims-account'] = accountData.account.softlayerAccountId;
      if (accountData !== null && accountData !== void 0 && (_accountData$account2 = accountData.account) !== null && _accountData$account2 !== void 0 && _accountData$account2.contextId) requestHeaders['ibm-account-context'] = accountData.account.contextId;
    }
    var opts = {
      headers: requestHeaders,
      method: method || 'GET',
      credentials: 'same-origin',
      signal: signal
    };
    // If a request body is provided and this is a PUT, POST, or PATCH, then add the body
    // to the request and assume content-type is json if not provided. Stringify the body
    // if we need to.
    if (body && ['put', 'post', 'patch'].find(function (m) {
      return m === opts.method.toLowerCase();
    })) {
      if (!opts.headers['content-type']) {
        opts.headers['content-type'] = 'application/json';
      }
      var stringify = opts.headers['content-type'].indexOf('application/json') > -1 && typeof body !== 'string';
      opts.body = stringify ? JSON.stringify(body) : body;
    }
    var isGet = opts.method.toLowerCase() === 'get';
    // Return the previously cached result for GET requests, if one exists and cahing is not
    // disabled. Anything other than a GET request will clear the entire cache.
    var key = getCacheKey(url, opts, cache);
    if (isGet) {
      var cachedResult = getCachedResult(key);
      if (cachedResult && !(cache !== null && cache !== void 0 && cache.disable)) return Promise.resolve(cachedResult);
    } else clearCache();
    // If the request is successful, attempt to resolve with the JSON body of the response. If the
    // request is not successful and the response has a JSON body, then add that JSON body to the
    // error object as responseJSON and re-throw it.
    return fetch(url, opts).then(function (result) {
      if (result.ok) {
        var clonedResponse = result.clone();
        return processResult(result)
        // return the original request as a last resort
        ["catch"](function () {
          return clonedResponse;
        }).then(function (data) {
          if (isGet) save(key, data, cache === null || cache === void 0 ? void 0 : cache.expiration);
          return data;
        });
      }
      throw result;
    })["catch"](function (err) {
      return (err.json ? err.json() : Promise.resolve()).then(function (json) {
        var errorResponse = err;
        errorResponse.responseJSON = json;
        throw errorResponse;
      })["catch"](function () {
        throw err;
      });
    });
  });
};
var get = function get(url, opts) {
  return send(_objectSpread2(_objectSpread2({
    url: url
  }, opts), {}, {
    method: 'GET'
  }));
};
var post = function post(url, opts) {
  return send(_objectSpread2(_objectSpread2({
    url: url
  }, opts), {}, {
    method: 'POST'
  }));
};
var put = function put(url, opts) {
  return send(_objectSpread2(_objectSpread2({
    url: url
  }, opts), {}, {
    method: 'PUT'
  }));
};
var patch = function patch(url, opts) {
  return send(_objectSpread2(_objectSpread2({
    url: url
  }, opts), {}, {
    method: 'PATCH'
  }));
};
var del = function del(url, opts) {
  return send(_objectSpread2(_objectSpread2({
    url: url
  }, opts), {}, {
    method: 'DELETE'
  }));
};

/**
 * Parse a given error response to obtain information to show in the Message component.
 * @param {Object} error The error response from a failed request.
 */
var parseError = function parseError(error) {
  var _error$errors, _data$extensions, _data$context, _data$extensions2, _caption;
  var status = error.status || error.status_code ? Number(error.status || error.status_code) : 0;
  var data = error.responseJSON || ((_error$errors = error.errors) === null || _error$errors === void 0 ? void 0 : _error$errors[0]) || _objectSpread2({}, error);
  var text = data.description || data.message ||
  // Assume that if the error is a string that starts with "<" then it's an HTML response
  // and we don't want to show that in the message.
  typeof error === 'string' && !error.startsWith('<') && error;
  var code = data.code || ((_data$extensions = data.extensions) === null || _data$extensions === void 0 ? void 0 : _data$extensions.code);
  var caption = data.incidentID || data.requestId || data.requestid || data['request-id'] || data.transaction_id || data.global_transaction_id || ((_data$context = data.context) === null || _data$context === void 0 ? void 0 : _data$context.requestId) || data.trace || ((_data$extensions2 = data.extensions) === null || _data$extensions2 === void 0 ? void 0 : _data$extensions2.trace) || code;
  // Fix issues where we have duplicate incident IDs separated by a comma
  if (typeof caption === 'string' && (_caption = caption) !== null && _caption !== void 0 && _caption.includes(',')) {
    var captionValues = caption.split(',').map(function (v) {
      return v.trim();
    });
    if (captionValues.length === 2 && captionValues[0] === captionValues[1]) {
      var _captionValues = _slicedToArray(captionValues, 1);
      caption = _captionValues[0];
    }
  }
  return {
    status: status,
    text: typeof text === 'string' ? text : null,
    caption: caption,
    code: code
  };
};
var request = {
  send: send,
  get: get,
  post: post,
  put: put,
  patch: patch,
  "delete": del,
  parseError: parseError
};

export { request as default, parseError, send };
